import { useEffect, useRef } from 'react';
import { persistence } from '@/persistence';
import { useFunnelStore } from '@/store/useFunnelStore';

const DEBOUNCE_MS = 750;

/**
 * Local-first autosave: edits apply instantly, the write is debounced, and
 * pagehide flushes whatever is still pending. No websockets and no conflict
 * resolution — concurrent editors are last-write-wins, which is survivable only
 * because every write is recorded in the revision log and so can be restored.
 */
export function useAutosave(scenarioId: string | null): void {
  const revision = useFunnelStore((s) => s.revision);
  const setStatus = useFunnelStore((s) => s.setStatus);
  const setDegraded = useFunnelStore((s) => s.setDegraded);
  const pending = useRef(false);

  useEffect(() => {
    if (!scenarioId || revision === 0) return;

    pending.current = true;
    const timer = window.setTimeout(async () => {
      const { params } = useFunnelStore.getState();
      setStatus('saving');
      try {
        await persistence.save(scenarioId, params);
        pending.current = false;
        setStatus('saved');
        setDegraded(false);
      } catch (error) {
        /* The load path refuses outright when the service is unreachable, so a
           session can only reach this point after a successful open — the
           service went away mid-session. The canvas keeps working, but the
           banner has to say so: edits are landing in localStorage only, and
           silently not saving is the failure mode worth shouting about. */
        setStatus('error', error instanceof Error ? error.message : 'Could not save');
        setDegraded(true);
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [scenarioId, revision, setStatus, setDegraded]);

  useEffect(() => {
    if (!scenarioId) return;
    const flush = () => {
      if (!pending.current) return;
      persistence.flush(scenarioId, useFunnelStore.getState().params);
      pending.current = false;
    };
    window.addEventListener('pagehide', flush);
    return () => {
      // Unmounting mid-edit should not silently drop the pending write.
      flush();
      window.removeEventListener('pagehide', flush);
    };
  }, [scenarioId]);
}
